import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { screenReviewXdb002Api, type SprintDashboard, type SprintValidation, type Xdb002Status } from '../../api/screen-review-xdb002';

type Tab = 'overview' | 'modules' | 'dimensions' | 'screens' | 'providers' | 'jobs' | 'gates' | 'status';

export function ScreenReviewXdb002Page() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<SprintDashboard | null>(null);
  const [modules, setModules] = useState<{ key: string; name: string; category: string }[]>([]);
  const [dimensions, setDimensions] = useState<{ key: string; name: string; category: string }[]>([]);
  const [screens, setScreens] = useState<{ key: string; name: string; category: string }[]>([]);
  const [providers, setProviders] = useState<{ key: string; name: string; interface: string }[]>([]);
  const [jobs, setJobs] = useState<{ key: string; name: string; schedule?: string }[]>([]);
  const [gates, setGates] = useState<{ key: string; name: string; blocking: boolean }[]>([]);
  const [status, setStatus] = useState<Xdb002Status | null>(null);
  const [validation, setValidation] = useState<SprintValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const card = 'border-rose-200 bg-white dark:border-rose-900/40 dark:bg-zinc-900';
  const muted = 'text-slate-500 dark:text-zinc-400';
  const accent = 'text-rose-700 dark:text-rose-300';

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, mod, dims, scr, prov, jb, gt, st] = await Promise.all([
        screenReviewXdb002Api.dashboard(), screenReviewXdb002Api.reviewModules(),
        screenReviewXdb002Api.reviewDimensions(), screenReviewXdb002Api.screens(),
        screenReviewXdb002Api.providers(), screenReviewXdb002Api.backgroundJobs(),
        screenReviewXdb002Api.qualityGates(), screenReviewXdb002Api.status(),
      ]);
      setDashboard(dash); setModules(mod); setDimensions(dims); setScreens(scr);
      setProviders(prov); setJobs(jb); setGates(gt); setStatus(st);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load sprint center'); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validate = async () => {
    setBusy(true); setActionMsg(null);
    try {
      const result = await screenReviewXdb002Api.validate({
        every_screen_audited: true, cognitive_load_validated: true,
        workflow_friction_documented: true, accessibility_review_passed: true,
        performance_review_passed: true, redesign_plan_complete: true,
      });
      setValidation(result);
      setActionMsg(result.valid ? 'Screen Review Bible passes all quality gates' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Validation failed'); }
    finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' }, { id: 'modules', label: 'Review Modules' },
    { id: 'dimensions', label: 'Review Dimensions' }, { id: 'screens', label: 'Screens' },
    { id: 'providers', label: 'Providers' }, { id: 'jobs', label: 'Jobs' },
    { id: 'gates', label: 'Quality Gates' }, { id: 'status', label: 'Status' },
  ];

  return (
    <div className="space-y-6 rounded-xl bg-rose-50/20 p-1 dark:bg-zinc-950">
      <div>
        <p className={`text-xs font-semibold uppercase ${accent}`}>XDB-000002 · P0 · Mandatory Before Internal Alpha</p>
        <h1 className="text-2xl font-semibold">Screen-by-Screen Experience Review Bible</h1>
        <p className={`text-sm ${muted}`}>Complete user journey review, screen audit, workflow optimization and human experience validation.</p>
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          <Link to="/platform/experience-design-xdb001" className={`${accent} hover:underline`}>XDB-000001</Link>
          <span className={muted}>→</span>
          <Link to="/screen-review" className={`${accent} hover:underline`}>Live UX Audit Command Center</Link>
        </div>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-700">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-rose-600 text-white' : `${card} border`}`}>{t.label}</button>
        ))}
        <button type="button" onClick={validate} disabled={busy} className="rounded-lg bg-rose-700 px-3 py-1.5 text-sm text-white disabled:opacity-50">Validate Sprint</button>
      </div>

      {tab === 'overview' && dashboard && (
        <div className="grid gap-3 sm:grid-cols-4">
          {[
            { label: 'Objectives', value: dashboard.objectives },
            { label: 'Review Modules', value: dashboard.review_modules },
            { label: 'Screens to Review', value: dashboard.screens_to_review },
            { label: 'Quality Gates', value: dashboard.quality_gates },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-2xl font-semibold">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'modules' && (
        <div className="grid gap-2 sm:grid-cols-2">
          {modules.map((m) => (
            <div key={m.key} className={`rounded-lg border p-3 ${card}`}>
              <p className="font-medium">{m.name}</p>
              <p className={`text-xs ${muted}`}>{m.category} · {m.key}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'dimensions' && (
        <div className="grid gap-2 sm:grid-cols-3">
          {dimensions.map((d) => (
            <div key={d.key} className={`rounded-lg border p-3 ${card}`}>
              <p className="font-medium">{d.name}</p>
              <p className={`text-xs ${muted}`}>{d.category}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'screens' && (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {screens.map((s) => (
            <div key={s.key} className={`rounded-lg border p-3 ${card}`}>
              <p className="font-medium">{s.name}</p>
              <p className={`text-xs ${muted}`}>{s.category}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'providers' && (
        <div className="grid gap-2">
          {providers.map((p) => (
            <div key={p.key} className={`rounded-lg border p-3 ${card}`}>
              <p className="font-medium">{p.name}</p>
              <p className={`text-xs font-mono ${muted}`}>{p.interface}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'jobs' && (
        <div className="grid gap-2 sm:grid-cols-2">
          {jobs.map((j) => (
            <div key={j.key} className={`rounded-lg border p-3 ${card}`}>
              <p className="font-medium">{j.name}</p>
              <p className={`text-xs ${muted}`}>{j.schedule ?? 'on-demand'}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'gates' && (
        <div className="space-y-2">
          {gates.map((g) => (
            <div key={g.key} className={`flex items-center justify-between rounded-lg border p-3 ${card}`}>
              <span>{g.name}</span>
              {g.blocking && <span className="text-xs text-red-600">blocking</span>}
            </div>
          ))}
          {validation && (
            <p className={`text-sm ${validation.valid ? 'text-rose-600' : 'text-red-600'}`}>
              Validation: {validation.valid ? 'PASS' : `FAIL — ${validation.gaps?.join(', ')}`}
            </p>
          )}
        </div>
      )}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <pre className="overflow-x-auto text-xs">{JSON.stringify(status, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}
